import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  CheckCircle2, ArrowLeft, Flame, Pickaxe, HardHat, Zap, Shield,
  XCircle, PauseCircle, PlayCircle,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { useReturnTo, withReturn } from "@/lib/returnTo";
import { useGps } from "@/features/gps/useGps";
import { GpsIndicator } from "@/features/gps/GpsIndicator";
import { Card, Pill, Button, Field, Input, Textarea, Select, Spinner } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { fmtDateTime } from "@/lib/dates";
import { SignaturePad, type SignaturePadHandle } from "@/features/signature/SignaturePad";

// =============================================================================
// Permit Issue + Detail screens
// =============================================================================

// Per-type field schema. Kept in code (not the DB) because permit rules are
// regulatory and shouldn't change accidentally via a misclick in an admin UI.
type TypeSchema = {
  label: string;
  icon: any;
  fields: Array<{
    key: string;
    label: string;
    type: "text" | "textarea" | "select" | "number" | "yesno" | "multi" | "heading";
    options?: string[];
    required?: boolean;
    help?: string;
  }>;
};

export const PERMIT_TYPES: Record<string, TypeSchema> = {
  hot_work: {
    label: "Hot Work", icon: Flame,
    fields: [
      { key: "fire_watch_name",     label: "Fire watch (name)",          type: "text",     required: true },
      { key: "extinguisher_type",   label: "Extinguisher on hand",       type: "select", required: true, options: ["Dry chem", "Water", "CO2", "Foam"] },
      { key: "combustibles_cleared", label: "Combustibles cleared 10m",  type: "yesno", required: true },
      { key: "gas_test_required",   label: "Gas test required?",          type: "yesno" },
      { key: "gas_test_result",     label: "Gas test result (LEL %)",     type: "text",  help: "If applicable." },
    ],
  },
  excavation: {
    label: "Excavation", icon: Pickaxe,
    fields: [
      { key: "depth_m",             label: "Excavation depth (m)",       type: "number",   required: true },
      { key: "dbyd_reference",      label: "Dial Before You Dig ref",    type: "text",     required: true, help: "Valid 28 days." },
      { key: "services_identified", label: "Underground services identified", type: "textarea", required: true },
      { key: "shoring_required",    label: "Shoring required?",          type: "yesno",    required: true, help: "≥1.5m usually requires shoring." },
      { key: "water_table_impacted", label: "Water table impacted?",     type: "yesno" },
    ],
  },
  workbox: {
    label: "Workbox / EWP", icon: HardHat,
    fields: [
      { key: "ewp_serial",          label: "EWP serial / asset #",       type: "text",     required: true },
      { key: "ewp_licence_op",      label: "Operator licence verified",  type: "yesno",    required: true },
      { key: "operator_above_11m",  label: "Boom over 11m?",             type: "yesno", help: "If yes, requires high-risk licence." },
      { key: "ground_conditions",   label: "Ground conditions ok?",      type: "yesno",    required: true },
      { key: "rescue_plan",         label: "Rescue plan",                type: "textarea", required: true },
    ],
  },
  confined_space: {
    label: "Confined Space", icon: Shield,
    fields: [
      { key: "space_description",   label: "Confined space description", type: "textarea", required: true },
      { key: "atmospheric_test",    label: "Atmospheric test done",      type: "yesno",    required: true },
      { key: "o2_level",            label: "O₂ level (%)",               type: "text" },
      { key: "lel_level",           label: "LEL (%)",                    type: "text" },
      { key: "h2s_ppm",             label: "H₂S (ppm)",                  type: "text" },
      { key: "standby_person",      label: "Standby person (name)",      type: "text",     required: true },
      { key: "rescue_plan",         label: "Rescue plan",                type: "textarea", required: true },
    ],
  },
  // The heights permit is THE permit for a tower company — benchmarked
  // against the Visress WAH permit James supplied (2026-08-03): training,
  // inspection & test, precautions, emergency response, fall prevention
  // methods, and a named permit holder.
  working_at_heights: {
    label: "Working at Heights", icon: HardHat,
    fields: [
      { key: "height_m",            label: "Max height (m)",             type: "number",   required: true },
      { key: "permit_holder",       label: "Permit holder (person in charge of the work)", type: "text", required: true, help: "They must also sign an acknowledgement once issued." },

      { key: "_h_training",         label: "Training & competency",      type: "heading" },
      { key: "crew_trained",        label: "All crew trained + competent for work at height", type: "yesno", required: true },
      { key: "rescue_trained",      label: "Rescue-trained personnel current (refresher ≤2 years)", type: "yesno", required: true },
      { key: "harness_trained",     label: "All personnel trained in fall-arrest harness + equipment", type: "yesno", required: true },

      { key: "_h_inspect",          label: "Inspection & test",          type: "heading" },
      { key: "restraint_considered", label: "Fall RESTRAINT considered over fall arrest (prevent the fall first)", type: "yesno", required: true },
      { key: "equipment_tagged",    label: "Fall equipment inspected, test tags current + in the WAH register", type: "yesno", required: true },
      { key: "anchor_points_rated", label: "Anchor points rated ≥15kN + inspected by a competent person",  type: "yesno", required: true },
      { key: "structure_capable",   label: "Structure capable of the intended load (incl. EWP)", type: "yesno", required: true },
      { key: "fall_arrest_in_use",  label: "Primary fall protection",    type: "select",   required: true, options: ["Harness + SRL", "Harness + shock lanyard", "Scaffold edge protection", "EWP only", "Rope access"] },

      { key: "_h_precautions",      label: "Precautions",                type: "heading" },
      { key: "pendulum_ok",         label: "Fall direction within 30° of vertical (no pendulum)", type: "yesno", required: true },
      { key: "dual_lanyard",        label: "Dual lanyard / 100% attachment — never detached at height", type: "yesno", required: true },
      { key: "drop_zone",           label: "Drop / exclusion zone established, demarcated + signed", type: "yesno", required: true },
      { key: "falling_objects",     label: "Falling objects prevented (tool lanyards, pockets checked)", type: "yesno", required: true },
      { key: "overheads_identified", label: "Overhead services identified + safe approach distances held", type: "yesno", required: true },
      { key: "access_egress",       label: "Safe access + egress to the work area", type: "yesno", required: true },
      { key: "prevention_methods",  label: "Fall prevention methods in use", type: "multi", options: ["Dual lanyard system", "Fall arrest harness", "Rope access / positioning", "Tool lanyards", "EWP", "Static line / LADSAF", "Edge protection"] },

      { key: "_h_emergency",        label: "Emergency response",         type: "heading" },
      { key: "rescue_plan",         label: "Rescue plan (method + steps)", type: "textarea", required: true, help: "Falls CoP: a suspended worker has minutes. Who gets them down, how?" },
      { key: "rescuer_name",        label: "Nominated rescuer (on site, briefed)", type: "text", required: true },
      { key: "rescue_equipment_onsite", label: "Rescue equipment on site + inspected", type: "yesno", required: true },
      { key: "two_way_comms",       label: "Two-way communication available", type: "yesno", required: true },
      { key: "rescuer_fit",         label: "Rescuers fit — no medical conditions affecting rescue", type: "yesno", required: true },
      { key: "weather_assessed",    label: "Weather assessed (wind, rain, lightning)", type: "yesno", required: true },
    ],
  },
  live_electrical: {
    label: "Live Electrical", icon: Zap,
    fields: [
      { key: "voltage",             label: "Voltage",                    type: "select",   required: true, options: ["ELV (<50V)", "LV (50V–1000V)", "HV (>1000V)"] },
      { key: "reason_for_live",     label: "Justification for live work", type: "textarea", required: true, help: "Why can't this be done de-energised?" },
      { key: "arc_rated_ppe",       label: "Arc-rated PPE in use",       type: "yesno",    required: true },
      { key: "second_person",       label: "Second person present",      type: "yesno",    required: true },
      { key: "isolation_possible",  label: "Isolation verified (if possible)", type: "yesno" },
    ],
  },
};

// -----------------------------------------------------------------------------
// Issue screen (new permit)
// -----------------------------------------------------------------------------
// T-426 — per-type validity defaults. Heights permits may run up to 7 days
// while the task runs (T-180 decision #9); everything else resets daily.
const VALIDITY_DAYS: Record<string, number> = { working_at_heights: 7 };

export function PermitIssueScreen() {
  const returnTo = useReturnTo();   // 27 Sep — opened from a Site folder? go back there
  const { permitType } = useParams<{ permitType: keyof typeof PERMIT_TYPES }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const gps = useGps(true);
  const schema = permitType ? PERMIT_TYPES[permitType] : null;
  const isDirector = hasRole(user, "director");

  const [project_id, setProjectId] = useState<string | null>(null);
  const [scope_description, setScope] = useState("");
  const [valid_from, setValidFrom] = useState(() => nowLocal());
  const [valid_until, setValidUntil] = useState(() => {
    const days = permitType ? (VALIDITY_DAYS[permitType] ?? 0) : 0;
    return days > 0 ? endOfDayLocal(days) : endOfDayLocal(0);
  });
  const [typeValues, setTypeValues] = useState<Record<string, unknown>>({});

  const { data: projects = [] } = useQuery({
    queryKey: ["projects_for_permit"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name, site_id")
        .in("status", ["live", "mobilising", "acceptance_pending"])
        .order("project_code");
      if (error) throw error;
      return data;
    },
  });

  const issueMut = useMutation({
    mutationFn: async () => {
      if (!permitType || !project_id) throw new Error("project_required");
      // T-426 lifecycle: directors approve+issue in one step; everyone else
      // REQUESTS (status draft) and a director approves from the detail
      // screen — segregation of duties, T-180 decision #3.
      const { data, error } = await supabase
        .from("permits")
        .insert({
          permit_type: permitType,
          project_id,
          site_id: (projects.find((p: any) => p.project_id === project_id) as any)?.site_id ?? null,
          issued_by_employee_id: user?.employee_id,
          issued_at: isDirector ? new Date().toISOString() : null,
          valid_from, valid_until,
          scope_description,
          type_specific_json: typeValues,
          status: isDirector ? "issued" : "draft",
          requested_by_user_id: user?.user_id ?? null,
          ...(isDirector ? { approved_by_user_id: user?.user_id ?? null, approved_at: new Date().toISOString() } : {}),
        })
        .select("permit_id")
        .single();
      if (error) throw error;
      return data.permit_id;
    },
    onSuccess: (id) => navigate(withReturn(`/permits/${id}`, returnTo)),
  });

  if (!schema) {
    return <div className="p-6 text-text-muted">Unknown permit type.</div>;
  }

  const Icon = schema.icon;
  const canSubmit =
    project_id && scope_description.trim() && valid_from && valid_until &&
    schema.fields.every(f => !f.required || typeValues[f.key] != null && typeValues[f.key] !== "");

  return (
    <div className="max-w-lg mx-auto p-4 pb-32">
      <button onClick={() => navigate(returnTo ?? "/permits")} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14}/> {returnTo ? "Site folder" : "Permits"}
      </button>

      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-md bg-accent/12 flex items-center justify-center">
          <Icon size={18} className="text-accent" />
        </div>
        <div>
          <div className="text-[11px] font-mono tracking-wider2 uppercase text-text-muted">New permit</div>
          <h1 className="text-[22px] font-semibold tracking-tight">{schema.label}</h1>
        </div>
      </div>

      <div className="flex items-center gap-3 text-[11px] text-text-muted mb-4">
        <GpsIndicator gps={gps} />
      </div>

      <Card>
        <div className="p-5 space-y-4">
          <Field label="Project">
            <Select value={project_id ?? ""} onChange={e => setProjectId(e.target.value || null)}>
              <option value="">— select project —</option>
              {projects.map((p: any) => (
                <option key={p.project_id} value={p.project_id}>{p.project_code} · {p.project_name}</option>
              ))}
            </Select>
          </Field>

          <Field label="Scope" hint="What work is this permit covering?">
            <Textarea rows={3} value={scope_description} onChange={e => setScope(e.target.value)} placeholder={`e.g. ${schema.label.toLowerCase()} on north side, 0800–1600`} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Valid from">
              <Input type="datetime-local" mono value={valid_from} onChange={e => setValidFrom(e.target.value)} />
            </Field>
            <Field label="Valid until">
              <Input type="datetime-local" mono value={valid_until} onChange={e => setValidUntil(e.target.value)} />
            </Field>
          </div>

          <div className="pt-3 border-t border-line space-y-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider2 text-text-muted">{schema.label} specifics</div>
            {schema.fields.map(f => (
              <TypeField key={f.key} f={f} value={typeValues[f.key]} onChange={v => setTypeValues(prev => ({ ...prev, [f.key]: v }))} />
            ))}
          </div>
        </div>
      </Card>

      <StickyBottomBar className="p-3 flex justify-center">
        <div className="flex gap-2 max-w-lg w-full">
          <Button variant="secondary" size="lg" onClick={() => navigate(returnTo ?? "/permits")}>Cancel</Button>
          <Button size="lg" icon={CheckCircle2} onClick={() => issueMut.mutate()} disabled={!canSubmit || issueMut.isPending} className="flex-1 justify-center">
            {issueMut.isPending ? "Saving…" : isDirector ? "Approve + issue permit" : "Request permit (director approves)"}
          </Button>
        </div>
      </StickyBottomBar>
    </div>
  );
}

function TypeField({ f, value, onChange }: { f: TypeSchema["fields"][number]; value: unknown; onChange: (v: unknown) => void }) {
  if (f.type === "heading") {
    return (
      <div className="pt-2 border-t border-line/40">
        <div className="text-[10px] font-mono uppercase tracking-wider2 text-accent">{f.label}</div>
      </div>
    );
  }
  if (f.type === "multi") {
    const arr = (value as string[] | undefined) ?? [];
    return (
      <Field label={f.label + (f.required ? " *" : "")} hint={f.help}>
        <div className="flex flex-wrap gap-1.5">
          {f.options?.map(o => {
            const on = arr.includes(o);
            return (
              <button key={o} type="button"
                onClick={() => onChange(on ? arr.filter(x => x !== o) : [...arr, o])}
                className={`text-[11px] px-2.5 py-1.5 rounded-full border transition ${on ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>
                {o}
              </button>
            );
          })}
        </div>
      </Field>
    );
  }
  return (
    <Field label={f.label + (f.required ? " *" : "")} hint={f.help}>
      {f.type === "text" && <Input value={(value as string) ?? ""} onChange={e => onChange(e.target.value)} />}
      {f.type === "number" && <Input type="number" mono value={(value as string) ?? ""} onChange={e => onChange(e.target.value)} />}
      {f.type === "textarea" && <Textarea rows={3} value={(value as string) ?? ""} onChange={e => onChange(e.target.value)} />}
      {f.type === "select" && (
        <Select value={(value as string) ?? ""} onChange={e => onChange(e.target.value || null)}>
          <option value="">— select —</option>
          {f.options?.map(o => <option key={o} value={o}>{o}</option>)}
        </Select>
      )}
      {f.type === "yesno" && (
        <div className="flex gap-2">
          {[[true, "Yes", "text-ok bg-ok/15 border-ok/30"], [false, "No", "text-danger bg-danger/15 border-danger/30"]].map(([v, label, active]) => (
            <button
              key={String(v)}
              type="button"
              onClick={() => onChange(v)}
              className={`flex-1 px-3 py-2 rounded border text-[12px] font-medium uppercase tracking-wider2 ${
                value === v ? active : "bg-ink-raised border-line text-text-muted hover:text-text"
              }`}
            >{label as string}</button>
          ))}
        </div>
      )}
    </Field>
  );
}

// -----------------------------------------------------------------------------
// Detail screen (existing permit)
// -----------------------------------------------------------------------------
export function PermitDetailScreen() {
  const returnTo = useReturnTo();
  const { permitId } = useParams<{ permitId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();

  const { data: permit, isLoading, refetch } = useQuery({
    queryKey: ["permit", permitId],
    enabled: !!permitId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("permits")
        .select(`
          permit_id, permit_number, permit_type, status,
          scope_description, valid_from, valid_until, location_on_site,
          type_specific_json, authorised_workers,
          issued_at, closed_at, suspension_reason,
          requested_by_user_id, approved_by_user_id, approved_at,
          issuer:employees!permits_issued_by_employee_id_fkey(first_name, last_name),
          projects(project_code, project_name),
          sites(site_code, site_name)
        `)
        .eq("permit_id", permitId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // T-426 — worker acknowledgements + the status audit trail
  const { data: acks = [], refetch: refetchAcks } = useQuery({
    queryKey: ["permit_acks", permitId],
    enabled: !!permitId,
    queryFn: async () => {
      const { data } = await supabase.from("permit_acks")
        .select("ack_id, name, company, role: source, signature_svg, acked_at")
        .eq("permit_id", permitId!).order("acked_at");
      return (data ?? []) as any[];
    },
  });
  const { data: timeline = [] } = useQuery({
    queryKey: ["permit_timeline", permitId, permit?.status],
    enabled: !!permitId,
    queryFn: async () => {
      const { data } = await supabase.from("permit_status_changes")
        .select("change_id, from_status, to_status, note, changed_at")
        .eq("permit_id", permitId!).order("changed_at");
      return (data ?? []) as any[];
    },
  });

  const statusMut = useMutation({
    mutationFn: async (update: Partial<{ status: string; closed_at: string; suspension_reason: string; closed_by_employee_id: string }>) => {
      const { error } = await supabase.from("permits").update(update).eq("permit_id", permitId!);
      if (error) throw error;
    },
    onSuccess: () => refetch(),
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Spinner size={20}/></div>;
  if (!permit) return <div className="p-6">Permit not found.</div>;

  const schema = PERMIT_TYPES[permit.permit_type] ?? null;
  const Icon = schema?.icon ?? Shield;
  const canAct = hasRole(user, "supervisor");
  const isDirector = hasRole(user, "director");
  const isExpired = permit.valid_until && new Date(permit.valid_until) < new Date()
    && !["closed", "cancelled"].includes(permit.status);

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6">
      <button onClick={() => navigate(returnTo ?? "/permits")} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14}/> {returnTo ? "Site folder" : "Permits"}
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-accent/12 flex items-center justify-center">
            <Icon size={18} className="text-accent" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[13px] text-accent">{permit.permit_number}</span>
              <Pill label={permit.status === "draft" ? "awaiting approval" : permit.status}
                tone={permit.status === "active" ? "live" : permit.status === "closed" ? "closed" : permit.status === "draft" ? "pending" : "mobilising"} />
              {isExpired && <Pill label="EXPIRED" tone="overdue" />}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight mt-1">{schema?.label ?? permit.permit_type}</h1>
            <div className="text-text-muted text-[13px] mt-0.5">
              {permit.projects?.project_code ?? "—"} · {permit.sites?.site_code ?? "—"} · Issued by {permit.issuer?.first_name} {permit.issuer?.last_name}
            </div>
          </div>
        </div>

        {canAct && permit.status !== "closed" && (
          <div className="flex items-center gap-2">
            {permit.status === "draft" && isDirector && (
              <Button icon={CheckCircle2} onClick={() => statusMut.mutate({
                status: "issued",
                approved_by_user_id: user?.user_id,
                approved_at: new Date().toISOString(),
                issued_at: new Date().toISOString(),
              } as any)}>Approve + issue</Button>
            )}
            {permit.status === "issued" && (
              <Button icon={PlayCircle} onClick={() => statusMut.mutate({ status: "active" })}>Activate</Button>
            )}
            {permit.status === "active" && (
              <Button variant="secondary" icon={PauseCircle} onClick={() => {
                const reason = prompt("Suspension reason");
                if (reason) statusMut.mutate({ status: "suspended", suspension_reason: reason });
              }}>Suspend</Button>
            )}
            {permit.status === "suspended" && (
              <Button icon={PlayCircle} onClick={() => statusMut.mutate({ status: "active" })}>Resume</Button>
            )}
            <Button variant="danger" icon={XCircle} onClick={() => {
              if (confirm("Close this permit? This can't be undone.")) {
                statusMut.mutate({ status: "closed", closed_at: new Date().toISOString(), closed_by_employee_id: user?.employee_id ?? undefined });
              }
            }}>Close out</Button>
          </div>
        )}
      </div>

      {permit.status === "draft" && (
        <div className="mb-4 bg-warn/10 border border-warn/40 rounded-md px-4 py-3 text-[13px] text-warn">
          <span className="font-medium">Awaiting director approval.</span> Work under this permit must not start
          until a director approves and issues it{isDirector ? " — that's you, button's above." : "."}
        </div>
      )}
      {isExpired && (
        <div className="mb-4 bg-danger/10 border border-danger/40 rounded-md px-4 py-3 text-[13px] text-danger">
          This permit's validity window has passed. If the work continues, issue a fresh permit — don't work under a dead one.
        </div>
      )}

      <Card className="mb-4">
        <div className="p-4 space-y-3">
          <KV label="Scope" value={permit.scope_description} />
          <KV label="Valid" value={`${fmtDateTime(permit.valid_from)} → ${fmtDateTime(permit.valid_until)}`} mono />
          {permit.location_on_site && <KV label="Location" value={permit.location_on_site} />}
          {permit.suspension_reason && <KV label="Suspension reason" value={permit.suspension_reason} />}
        </div>
      </Card>

      {schema && permit.type_specific_json && (
        <Card>
          <div className="p-4 border-b border-line">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">{schema.label} specifics</div>
          </div>
          <div className="p-4 space-y-3">
            {schema.fields.map(f => f.type === "heading" ? (
              <div key={f.key} className="pt-2 border-t border-line/40 text-[10px] font-mono uppercase tracking-wider2 text-accent">{f.label}</div>
            ) : (
              <KV
                key={f.key}
                label={f.label}
                value={formatVal(permit.type_specific_json[f.key])}
                mono={f.type === "number"}
              />
            ))}
          </div>
        </Card>
      )}

      {/* T-426 — worker acknowledgements: nobody works under a permit they
          haven't signed onto. */}
      <Card className="mt-4">
        <div className="p-4 border-b border-line flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Acknowledged by · {acks.length}</div>
        </div>
        <div className="p-4 space-y-2">
          {acks.length === 0 && (
            <div className="text-[12px] text-text-muted italic">Nobody has acknowledged this permit yet — everyone working under it signs here.</div>
          )}
          {acks.map((a: any) => (
            <div key={a.ack_id} className="bg-ink rounded-md border border-line/50 px-3 py-2 flex items-center gap-3">
              <CheckCircle2 size={14} className="text-ok flex-shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-text">{a.name}{a.company ? <span className="text-text-muted"> · {a.company}</span> : ""}</div>
                <div className="text-[11px] text-text-muted">acknowledged {fmtDateTime(a.acked_at)}</div>
              </div>
              {a.signature_svg && (
                <span className="w-[90px] h-[30px] bg-white rounded border border-line overflow-hidden [&>svg]:w-full [&>svg]:h-full"
                  dangerouslySetInnerHTML={{ __html: a.signature_svg }}/>
              )}
            </div>
          ))}
          {["issued", "active"].includes(permit.status) && !isExpired && (
            <PermitAckAdder permitId={permit.permit_id} onAdded={() => refetchAcks()}/>
          )}
          {permit.status === "draft" && (
            <div className="text-[11px] text-text-muted">Acknowledgements open once a director issues the permit.</div>
          )}
        </div>
      </Card>

      {/* Status timeline */}
      {timeline.length > 0 && (
        <Card className="mt-4">
          <div className="p-4 border-b border-line">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">History</div>
          </div>
          <div className="p-4 space-y-1.5">
            {timeline.map((t: any) => (
              <div key={t.change_id} className="text-[12px] flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[11px] text-text-muted w-[130px]">{fmtDateTime(t.changed_at)}</span>
                <span className="text-text">
                  {t.from_status ? `${t.from_status === "draft" ? "requested" : t.from_status} → ` : ""}
                  {t.to_status === "draft" ? "requested" : t.to_status}
                </span>
                {t.note && <span className="text-text-muted">· {t.note}</span>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Acknowledge adder — mirrors the start-card sign-on (pass the phone around)
// -----------------------------------------------------------------------------
function PermitAckAdder({ permitId, onAdded }: { permitId: string; onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const sigRef = React.useRef<SignaturePadHandle | null>(null);

  async function save() {
    if (name.trim().length < 2) { setErr("Name required."); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { setErr("Signature required."); return; }
    setBusy(true); setErr(null);
    try {
      const { error } = await supabase.from("permit_acks").insert({
        permit_id: permitId,
        name: name.trim(),
        company: company.trim() || null,
        source: "other",
        signature_svg: sigRef.current.getSvg(),
        device_info: { ua: navigator.userAgent },
      });
      if (error) throw error;
      setName(""); setCompany(""); setOpen(false);
      onAdded();
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>Acknowledge this permit</Button>;
  }
  return (
    <div className="bg-ink-raised/40 border border-dashed border-line rounded-md p-3 space-y-3">
      {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}
      <div className="text-[12px] text-text-muted">
        By signing you confirm you've been inducted on this permit's requirements and will work to its conditions.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input placeholder="Full name" value={name} onChange={e => setName(e.target.value)} autoFocus/>
        <Input placeholder="Company" value={company} onChange={e => setCompany(e.target.value)}/>
      </div>
      <SignaturePad ref={sigRef} width={420} height={120}/>
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={busy}>{busy ? "Saving…" : "Sign"}</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}

function KV({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[140px_1fr] items-start gap-3">
      <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium pt-1">{label}</div>
      <div className={`text-[13px] text-text ${mono ? "font-mono" : ""}`}>{value ?? <span className="text-text-dim">—</span>}</div>
    </div>
  );
}

function formatVal(v: unknown): React.ReactNode {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (v == null) return null;
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

function nowLocal(offsetHours = 0): string {
  const d = new Date(Date.now() + offsetHours * 3600_000);
  d.setSeconds(0, 0);
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
}

function endOfDayLocal(plusDays = 0): string {
  const d = new Date(Date.now() + plusDays * 86400_000);
  d.setHours(23, 59, 0, 0);
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
}
